import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { api } from '@/hooks/useAuth';
import { useAppointmentTypes } from '@/hooks/useAppointmentTypes';
import { UserCheck, Users } from 'lucide-react';

export default function AppointmentDialog({ open, onClose, teams, selectedDate, onSubmit, isManagerOrAdmin = false }) {
  const { types, TYPE_ICONS } = useAppointmentTypes();
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    team_id: '',
    date: '',
    start_time: '',
    end_time: '',
    duration: 60,
    location: '',
    address: '',
    appointment_type: 'other',
    assigned_to_user_id: '',
    assigned_to_team_id: ''
  });
  const [loading, setLoading] = useState(false);
  const [technicians, setTechnicians] = useState([]);

  // Fetch technicians for assignment dropdown
  useEffect(() => {
    const fetchTechnicians = async () => {
      if (isManagerOrAdmin) {
        try {
          const response = await api.get('/users/technicians');
          setTechnicians(response.data);
        } catch (error) {
          console.log('Could not fetch technicians:', error);
        }
      }
    };
    fetchTechnicians();
  }, [isManagerOrAdmin]);

  useEffect(() => {
    if (selectedDate) {
      const dateStr = `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.getDate()).padStart(2, '0')}`;
      setFormData(prev => ({ ...prev, date: dateStr }));
    }
  }, [selectedDate]);

  useEffect(() => {
    if (formData.start_time && formData.duration) {
      const [hours, minutes] = formData.start_time.split(':').map(Number);
      const startMinutes = hours * 60 + minutes;
      const endMinutes = startMinutes + parseInt(formData.duration);
      const endHours = Math.floor(endMinutes / 60);
      const endMins = endMinutes % 60;
      setFormData(prev => ({
        ...prev,
        end_time: `${String(endHours).padStart(2, '0')}:${String(endMins).padStart(2, '0')}`
      }));
    }
  }, [formData.start_time, formData.duration]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Clean up empty assignment fields
      const submitData = { ...formData };
      if (!submitData.assigned_to_user_id) delete submitData.assigned_to_user_id;
      if (!submitData.assigned_to_team_id) delete submitData.assigned_to_team_id;
      
      await onSubmit(submitData);
      setFormData({
        title: '',
        description: '',
        team_id: '',
        date: '',
        start_time: '',
        end_time: '',
        duration: 60,
        location: '',
        address: '',
        appointment_type: 'other',
        assigned_to_user_id: '',
        assigned_to_team_id: ''
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px] max-h-[85vh]" data-testid="appointment-dialog" aria-describedby="appointment-form-description">
        <DialogHeader>
          <DialogTitle className="text-xl sm:text-2xl font-bold text-[#0f172a]">Create Appointment</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="space-y-3 sm:space-y-4 py-2 sm:py-4">
            <div className="space-y-2">
              <Label htmlFor="title" className="text-sm">Title</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
                placeholder="Meeting with client"
                data-testid="appointment-title-input"
                className="text-base"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="team" className="text-sm">Team</Label>
              <Select
                value={formData.team_id}
                onValueChange={(value) => setFormData({ ...formData, team_id: value })}
                required
              >
                <SelectTrigger data-testid="team-select" className="text-base">
                  <SelectValue placeholder="Select a team" />
                </SelectTrigger>
                <SelectContent>
                  {teams.map((team) => (
                    <SelectItem key={team.id} value={team.id} data-testid={`team-option-${team.name.toLowerCase().replace(/\s+/g, '-')}`}>
                      <div className="flex items-center gap-2">
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: team.color }}
                        ></div>
                        {team.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="appointment_type" className="text-sm">Appointment Type</Label>
              <Select
                value={formData.appointment_type}
                onValueChange={(value) => setFormData({ ...formData, appointment_type: value })}
                required
              >
                <SelectTrigger data-testid="appointment-type-select" className="text-base">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {types.map((type) => {
                    const IconComponent = TYPE_ICONS[type.value] || TYPE_ICONS.other;
                    return (
                      <SelectItem key={type.value} value={type.value} data-testid={`type-option-${type.value}`}>
                        <div className="flex items-center gap-2">
                          <div 
                            className="w-6 h-6 rounded flex items-center justify-center"
                            style={{ backgroundColor: type.bgColor }}
                          >
                            <IconComponent size={14} style={{ color: type.color }} />
                          </div>
                          <span>{type.label}</span>
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="date" className="text-sm">Date</Label>
              <Input
                id="date"
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                required
                data-testid="appointment-date-input"
                className="text-base"
              />
            </div>

            <div className="grid grid-cols-2 gap-3 sm:gap-4">
              <div className="space-y-2">
                <Label htmlFor="start_time" className="text-sm">Start Time</Label>
                <Input
                  id="start_time"
                  type="time"
                  value={formData.start_time}
                  onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                  required
                  data-testid="appointment-start-time-input"
                  className="text-base"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="duration" className="text-sm">Duration (min)</Label>
                <Input
                  id="duration"
                  type="number"
                  min="15"
                  step="15"
                  value={formData.duration}
                  onChange={(e) => setFormData({ ...formData, duration: parseInt(e.target.value) })}
                  required
                  data-testid="appointment-duration-input"
                  className="text-base"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description" className="text-sm">Description (Optional)</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Add details about the appointment"
                rows={2}
                data-testid="appointment-description-input"
                className="text-base"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="location" className="text-sm">Location Name (Optional)</Label>
              <Input
                id="location"
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                placeholder="Conference Room A"
                data-testid="appointment-location-input"
                className="text-base"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="address" className="text-sm">Address (Optional)</Label>
              <Textarea
                id="address"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="123 Main St, City, State, ZIP"
                rows={2}
                data-testid="appointment-address-input"
                className="text-base"
              />
            </div>

            {/* Assignment Section - Only for Managers/Admins */}
            {isManagerOrAdmin && (
              <div className="pt-3 sm:pt-4 border-t border-slate-200">
                <Label className="text-sm font-semibold text-slate-700 mb-2 block">Assignment (Required for Completion)</Label>
                <p className="text-xs text-slate-500 mb-3">Assign to a technician or team.</p>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="assigned_user" className="flex items-center gap-1 text-sm">
                      <UserCheck size={14} />
                      Technician
                    </Label>
                    <Select
                      value={formData.assigned_to_user_id}
                      onValueChange={(value) => setFormData({ ...formData, assigned_to_user_id: value === 'none' ? '' : value })}
                    >
                      <SelectTrigger data-testid="assign-user-select" className="text-base">
                        <SelectValue placeholder="Select technician" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">-- None --</SelectItem>
                        {technicians.map((tech) => (
                          <SelectItem key={tech.id} value={tech.id}>
                            {tech.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="assigned_team" className="flex items-center gap-1 text-sm">
                      <Users size={14} />
                      Team
                    </Label>
                    <Select
                      value={formData.assigned_to_team_id}
                      onValueChange={(value) => setFormData({ ...formData, assigned_to_team_id: value === 'none' ? '' : value })}
                    >
                      <SelectTrigger data-testid="assign-team-select" className="text-base">
                        <SelectValue placeholder="Select team" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">-- None --</SelectItem>
                        {teams.map((team) => (
                          <SelectItem key={team.id} value={team.id}>
                            <div className="flex items-center gap-2">
                              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: team.color }}></div>
                              {team.name}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} data-testid="cancel-button">
              Cancel
            </Button>
            <Button
              type="submit"
              className="bg-[#2563eb] hover:bg-[#1d4ed8] text-white"
              disabled={loading}
              data-testid="create-appointment-button"
            >
              {loading ? 'Creating...' : 'Create Appointment'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}